/**
 * Shared visibility and interactability utilities for DOM capture layers.
 *
 * Both `DOMIntrospector` (dom-capture.ts) and `AccessibleSnapshotCapture`
 * (accessible-snapshot.ts) need to determine modal state, collapsed state,
 * and element interactability. This module centralises that logic so the
 * two capture paths stay consistent and are easier to maintain.
 */

// ---------------------------------------------------------------------------
// Active-modal cache
// ---------------------------------------------------------------------------

let _activeModalCache: Element[] | null = null;
let _activeModalCacheTime: number = 0;

/**
 * Invalidate the cached modal list.
 * Called by MutationObserver callbacks in the consuming modules.
 */
export function invalidateModalCache(): void {
  _activeModalCache = null;
}

/**
 * Return currently visible modal/dialog elements.
 * The result is cached for up to 500 ms and can be invalidated
 * instantly via {@link invalidateModalCache}.
 */
export function getActiveModals(): Element[] {
  if (_activeModalCache && Date.now() - _activeModalCacheTime < 500) {
    return _activeModalCache;
  }

  const modals = document.querySelectorAll(
    '[aria-modal="true"]:not([aria-hidden="true"]), ' +
    '[role="dialog"]:not([aria-hidden="true"]), ' +
    'dialog[open]'
  );
  _activeModalCache = Array.from(modals).filter(m => {
    const s = window.getComputedStyle(m);
    return s.display !== 'none' && s.visibility !== 'hidden';
  });
  _activeModalCacheTime = Date.now();
  return _activeModalCache;
}

// ---------------------------------------------------------------------------
// Active overlay detection (dropdowns, menus, popovers)
// ---------------------------------------------------------------------------

/** Selectors for overlay containers that render outside the main DOM tree. */
const OVERLAY_CONTAINER_SELECTORS = [
  // Angular CDK overlay containers
  '.cdk-overlay-container',
  '.cdk-overlay-pane',
  // Ant Design / NG-ZORRO dropdowns
  '.ant-select-dropdown:not(.ant-select-dropdown-hidden)',
  '.ant-dropdown:not(.ant-dropdown-hidden)',
  '.ant-popover:not(.ant-popover-hidden)',
  '.ant-picker-dropdown',
  '.ant-cascader-dropdown',
  '.ant-tree-select-dropdown',
  // Generic ARIA overlay patterns
  '[role="listbox"]:not([aria-hidden="true"])',
  '[role="menu"]:not([aria-hidden="true"])',
  '[role="tree"]:not([aria-hidden="true"])',
  // React Select / Headless UI / Radix
  '[class*="MenuList"]',
  '[class*="menu"][class*="--is-open"]',
  '[data-radix-popper-content-wrapper]',
  '[data-headlessui-state*="open"]',
  // MUI Popper / Popover
  '.MuiPopper-root',
  '.MuiPopover-root',
  '.MuiMenu-root',
  '.MuiAutocomplete-popper',
  // Generic popover/tooltip containers
  '[class*="popover"]:not([aria-hidden="true"])',
  '[class*="Popover"]:not([aria-hidden="true"])',
];

let _activeOverlayCache: Element[] | null = null;
let _activeOverlayCacheTime: number = 0;

/** Invalidate the cached overlay list (call on DOM mutation). */
export function invalidateOverlayCache(): void {
  _activeOverlayCache = null;
}

/**
 * Return currently visible overlay containers (dropdowns, menus, popovers).
 * Cached for up to 300 ms (shorter than modals since overlays are transient).
 */
export function getActiveOverlays(): Element[] {
  if (_activeOverlayCache && Date.now() - _activeOverlayCacheTime < 300) {
    return _activeOverlayCache;
  }

  const overlays: Element[] = [];
  const seen = new Set<Element>();

  for (const selector of OVERLAY_CONTAINER_SELECTORS) {
    try {
      const els = Array.from(document.querySelectorAll(selector));
      for (const el of els) {
        if (seen.has(el)) continue;
        const htmlEl = el as HTMLElement;
        try {
          const s = window.getComputedStyle(htmlEl);
          if (s.display === 'none' || s.visibility === 'hidden') continue;
          const rect = htmlEl.getBoundingClientRect();
          // Must have some visual size to be considered active
          if (rect.width > 5 && rect.height > 5) {
            overlays.push(el);
            seen.add(el);
          }
        } catch { /* skip */ }
      }
    } catch { /* invalid selector, skip */ }
  }

  _activeOverlayCache = overlays;
  _activeOverlayCacheTime = Date.now();
  return _activeOverlayCache;
}

/**
 * Return `true` if `el` is inside an active overlay container.
 */
export function isInsideActiveOverlay(el: HTMLElement): boolean {
  const overlays = getActiveOverlays();
  return overlays.some(o => o.contains(el));
}

// ---------------------------------------------------------------------------
// Element-level checks
// ---------------------------------------------------------------------------

/**
 * Return `true` if `el` is inside an active modal's subtree
 * or is an ancestor that contains a modal (e.g. the `<body>`).
 * When no modals are open this always returns `true`.
 */
export function isAllowedByModals(el: HTMLElement): boolean {
  const modals = getActiveModals();
  if (modals.length === 0) return true;
  const insideAny = modals.some(m => m.contains(el));
  const containsAny = modals.some(m => el.contains(m));
  if (insideAny || containsAny) return true;
  // Elements inside active overlays (dropdowns, menus) are always allowed
  // even when a modal is present — they may be dropdown options within the modal
  if (isInsideActiveOverlay(el)) return true;
  return false;
}

/**
 * Return `true` when `el` is hidden by `aria-hidden="true"` on itself
 * or on an ancestor.
 */
export function isAriaHidden(el: HTMLElement): boolean {
  return el.getAttribute('aria-hidden') === 'true' ||
    !!el.closest('[aria-hidden="true"]');
}

/**
 * Return `true` if `el` is content inside a collapsed `<details>` element
 * (not the `<details>` itself or its `<summary>`).
 */
export function isInsideCollapsedDetails(el: HTMLElement): boolean {
  const closedDetails = el.closest('details:not([open])');
  if (!closedDetails || el === closedDetails) return false;
  const summary = closedDetails.querySelector(':scope > summary');
  return !(summary?.contains(el)) && el !== summary;
}

/**
 * Return `true` if `el` is inside a collapsed accordion panel
 * (an ancestor has `aria-expanded="false"` and `el` is in the
 * associated content panel).
 *
 * Walks up to `maxDepth` ancestors.
 */
export function isInsideCollapsedAccordion(el: HTMLElement, maxDepth: number = 10): boolean {
  let ancestor = el.parentElement;
  let depth = 0;
  while (ancestor && depth < maxDepth) {
    if (ancestor.getAttribute('aria-expanded') === 'false') {
      const nextSibling = ancestor.nextElementSibling;
      if (nextSibling && nextSibling.contains(el)) return true;

      const panel = ancestor.querySelector(
        '[role="tabpanel"], [role="region"], .panel, .accordion-content, .collapse:not(.show)'
      );
      if (panel && panel.contains(el)) return true;
    }
    ancestor = ancestor.parentElement;
    depth++;
  }
  return false;
}

/**
 * Check if an element can actually receive user interaction right now.
 *
 * @param el              The element to check
 * @param isInteractive   When `false` the expensive `elementFromPoint`
 *                        occlusion test is skipped (non-interactive elements
 *                        don't need it).
 */
export function checkInteractable(el: HTMLElement, isInteractive: boolean = true): boolean {
  // Disabled state
  if ((el as HTMLInputElement).disabled || el.getAttribute('aria-disabled') === 'true') {
    return false;
  }

  // Behind a modal
  if (!isAllowedByModals(el)) return false;

  // pointer-events: none
  try {
    const style = window.getComputedStyle(el);
    if (style.pointerEvents === 'none') return false;
  } catch { /* ignore */ }

  // Occlusion (expensive — only for interactive elements)
  // Skip occlusion test for elements inside active overlays (CDK overlays,
  // dropdowns, popovers) — elementFromPoint often returns the backdrop/pane
  // instead of the actual element, causing false negatives.
  if (isInteractive && !isInsideActiveOverlay(el)) {
    try {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        if (centerX >= 0 && centerX <= window.innerWidth &&
            centerY >= 0 && centerY <= window.innerHeight) {
          const topElement = document.elementFromPoint(centerX, centerY);
          if (topElement && topElement !== el &&
              !el.contains(topElement) && !topElement.contains(el)) {
            return false;
          }
        }
      }
    } catch { /* ignore */ }
  }

  return true;
}
